import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, Link, X } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { Contenuto, StatoContenuto, PiattaformaSocial } from '../types';
import StatusBadge from '../components/StatusBadge';
import { format } from 'date-fns';
import PageHeader from '../components/ui/PageHeader';
import StatsCard from '../components/ui/StatsCard';
import { usePageState } from '../hooks/usePageState';

const Calendar: React.FC = () => {
  const { state, getClienteById } = useData();
  // Filtri nell'URL: si ritrovano identici dopo indietro/refresh
  const [pageState, setPageState] = usePageState({ stato: '', piattaforma: '' });
  const filtroStato = pageState.stato as StatoContenuto | '';
  const setFiltroStato = (value: StatoContenuto | '') => setPageState({ stato: value });
  const filtroPiattaforma = pageState.piattaforma as PiattaformaSocial | '';
  const setFiltroPiattaforma = (value: PiattaformaSocial | '') => setPageState({ piattaforma: value });
  
  // Mobile-specific states
  const [isMobile, setIsMobile] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Debug: log dei contenuti
  console.log('📅 Contenuti totali nel sistema:', state.contenuti.length);
  console.log('📅 Contenuti con dataPubblicazione:', state.contenuti.filter(c => c.dataPubblicazione).length);
  console.log('📅 Contenuti per stato:', state.contenuti.reduce((acc, c) => {
    acc[c.stato] = (acc[c.stato] || 0) + 1;
    return acc;
  }, {} as Record<string, number>));
  
  // Debug dettagliato di tutti i contenuti
  console.log('📅 Tutti i contenuti:', state.contenuti.map(c => ({
    id: c.id,
    titolo: c.titolo,
    stato: c.stato,
    dataPubblicazione: c.dataPubblicazione,
    clienteId: c.clienteId,
    cliente: getClienteById(c.clienteId)?.nomeAzienda
  })));
  
  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Filtra contenuti per il calendario - INCLUDI ANCHE CONTENUTI ARCHIVIATI/PUBBLICATI
  const contenutiCalendario = state.contenuti.filter(contenuto => {
    console.log(`🔍 Analizzando contenuto ${contenuto.id}:`, {
      titolo: contenuto.titolo,
      dataPubblicazione: contenuto.dataPubblicazione,
      stato: contenuto.stato,
      clienteId: contenuto.clienteId,
      hook: contenuto.hook,
      body: contenuto.body,
      cta: contenuto.cta
    });
    
    // PER CONTENUTI PUBBLICATI/ARCHIVIATI: mostrali SEMPRE anche se non hanno dataPubblicazione (bug del cron)
    const isArchivedOrPublished = contenuto.stato === StatoContenuto.PUBBLICATO || contenuto.stato === StatoContenuto.ARCHIVIATO;
    
    // MOSTRA TUTTI I CONTENUTI CON DATA DI PUBBLICAZIONE + CONTENUTI ARCHIVIATI/PUBBLICATI ANCHE SENZA DATA
    if (!contenuto.dataPubblicazione && !isArchivedOrPublished) {
      console.log(`❌ Contenuto ${contenuto.id} escluso: nessuna data di pubblicazione e non è archiviato/pubblicato`);
      return false;
    }
    
    if (!contenuto.dataPubblicazione && isArchivedOrPublished) {
      console.log(`⚠️ Contenuto archiviato/pubblicato ${contenuto.id} senza data - INCLUSO comunque per bug fix`);
    }
    
    // Verifica che il cliente esista
    const cliente = getClienteById(contenuto.clienteId);
    if (!cliente) {
      console.log(`❌ Contenuto ${contenuto.id} escluso: cliente non trovato`);
      return false;
    }
    
    if (!isArchivedOrPublished) {
      // Solo per contenuti non pubblicati/archiviati, verifica le informazioni essenziali
      const hasEssentialInfo = contenuto.titolo && 
                              contenuto.hook && 
                              contenuto.body && 
                              contenuto.cta &&
                              contenuto.titolo.trim() !== '' &&
                              contenuto.hook.trim() !== '' &&
                              contenuto.body.trim() !== '' &&
                              contenuto.cta.trim() !== '';
      
      if (!hasEssentialInfo) {
        console.log(`❌ Contenuto ${contenuto.id} escluso: mancano informazioni essenziali (non pubblicato/archiviato)`);
        return false;
      }
      
      // Verifica che il cliente abbia piattaforme configurate (solo per contenuti non pubblicati)
      if (!cliente.piattaforme || cliente.piattaforme.length === 0) {
        console.log(`❌ Contenuto ${contenuto.id} escluso: cliente senza piattaforme configurate`);
        return false;
      }
    } else {
      console.log(`✅ Contenuto ${contenuto.id} incluso: pubblicato/archiviato (${contenuto.stato})`);
    }
    
    const matchStato = !filtroStato || contenuto.stato === filtroStato;
    const matchPiattaforma = !filtroPiattaforma || (cliente?.piattaforme?.includes(filtroPiattaforma) ?? false);
    
    console.log(`🔍 Filtri per contenuto ${contenuto.id}:`, {
      matchStato,
      matchPiattaforma,
      filtroStato,
      filtroPiattaforma,
      isArchivedOrPublished
    });
    
    const result = matchStato && matchPiattaforma;
    console.log(`✅ Contenuto ${contenuto.id} ${result ? 'incluso' : 'escluso'}`);
    
    return result;
  });
  
  console.log('📅 Contenuti filtrati per calendario:', contenutiCalendario.length);
  console.log('📅 Contenuti filtrati dettagli:', contenutiCalendario.map(c => ({
    id: c.id,
    titolo: c.titolo,
    clienteId: c.clienteId,
    stato: c.stato,
    dataPubblicazione: c.dataPubblicazione,
    cliente: getClienteById(c.clienteId)?.nomeAzienda
  })));

  // Applica filtri (TEMPORANEO: disabilitato per debug)
  const contenutiFiltrati = contenutiCalendario;
  
  /*
  const contenutiFiltrati = contenutiCalendario.filter(contenuto => {
    const cliente = getClienteById(contenuto.clienteId);
    const matchStato = !filtroStato || contenuto.stato === filtroStato;
    const matchPiattaforma = !filtroPiattaforma || (cliente?.piattaforme?.includes(filtroPiattaforma) ?? false);
    
    return matchStato && matchPiattaforma;
  });
  */

  // Ordina per data di pubblicazione (più recenti prima) e poi per data di aggiornamento
  const contenutiOrdinati = contenutiFiltrati.sort((a, b) => {
    // Se entrambi hanno dataPubblicazione, ordina per quella
    if (a.dataPubblicazione && b.dataPubblicazione) {
      return new Date(b.dataPubblicazione).getTime() - new Date(a.dataPubblicazione).getTime();
    }
    
    // Se solo uno ha dataPubblicazione, quello va prima
    if (a.dataPubblicazione && !b.dataPubblicazione) return -1;
    if (!a.dataPubblicazione && b.dataPubblicazione) return 1;
    
    // Se nessuno ha dataPubblicazione, ordina per data di aggiornamento (per contenuti archiviati)
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

  const resetFilters = () => {
    setPageState({ stato: '', piattaforma: '' });
  };

  return (
    <div className="w-full min-w-0 max-w-full overflow-x-hidden space-y-4 sm:space-y-6" style={{boxSizing: 'border-box', maxWidth: '100vw', width: '100%'}}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <PageHeader
          title="Calendario Editoriale"
          description="Visualizza i contenuti programmati"
        />
        <StatsCard
          label="Contenuti"
          value={contenutiOrdinati.length}
          variant="brand"
          size="sm"
        />
      </div>

      {/* Responsive Compact Filters */}
      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 sm:gap-3 mb-4">
        <div className="flex flex-1 sm:flex-none gap-2">
          <select
            value={filtroStato}
            onChange={(e) => setFiltroStato(e.target.value as StatoContenuto | '')}
            className="flex-1 sm:flex-none px-3 py-2.5 sm:py-2 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 min-w-0 sm:min-w-[140px]"
          >
            <option value="">Tutti gli stati</option>
            {Object.values(StatoContenuto).map(stato => (
              <option key={stato} value={stato}>{stato}</option>
            ))}
          </select>

          <select
            value={filtroPiattaforma}
            onChange={(e) => setFiltroPiattaforma(e.target.value as PiattaformaSocial | '')}
            className="flex-1 sm:flex-none px-3 py-2.5 sm:py-2 bg-white border border-slate-200 rounded-xl text-sm text-slate-900 focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 min-w-0 sm:min-w-[140px]"
          >
            <option value="">Piattaforma</option>
            {Object.values(PiattaformaSocial).map(piattaforma => (
              <option key={piattaforma} value={piattaforma}>{piattaforma}</option>
            ))}
          </select>
        </div>

        {(filtroStato || filtroPiattaforma) && (
          <button
            onClick={resetFilters}
            className="flex items-center justify-center gap-2 p-2.5 sm:p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 active:bg-slate-200 transition-all"
            title="Pulisci filtri"
          >
            <X className="w-4 h-4" />
            <span className="sm:hidden text-sm">Pulisci filtri</span>
          </button>
        )}
      </div>

      {/* Responsive content list */}
      <div className="glass-card overflow-hidden">
        {contenutiOrdinati.length === 0 ? (
          <div className="px-4 sm:px-6 py-8 sm:py-12 text-center">
            <div className="flex flex-col items-center space-y-4">
              <div className="p-3 sm:p-4 bg-gray-100 rounded-full">
                <CalendarIcon className="h-6 w-6 sm:h-8 sm:w-8 md:h-10 md:w-10 text-gray-400" />
              </div>
              <div className="space-y-1.5">
                <h3 className="text-sm sm:text-base font-medium text-gray-900">
                  Nessun contenuto programmato
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 max-w-md mx-auto leading-relaxed px-4">
                  I contenuti appariranno qui quando verranno assegnati a una data di pubblicazione.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {contenutiOrdinati.map((contenuto) => {
              const cliente = getClienteById(contenuto.clienteId);

              return (
                <div
                  key={contenuto.id}
                  className="p-3 sm:p-4 md:p-6 transition-all hover:bg-gray-50 active:bg-gray-100 w-full min-w-0 overflow-hidden"
                >
                  {/* Responsive timestamp and status */}
                  <div className="flex flex-col xs:flex-row justify-between items-start xs:items-center gap-2 text-xs text-gray-400 mb-3">
                    {contenuto.dataPubblicazione ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] sm:text-xs font-medium bg-green-100 text-green-800">
                        <CalendarIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{format(new Date(contenuto.dataPubblicazione), isMobile ? 'dd/MM/yy HH:mm' : 'dd/MM/yyyy HH:mm')}</span>
                      </span>
                    ) : (contenuto.stato === StatoContenuto.ARCHIVIATO || contenuto.stato === StatoContenuto.PUBBLICATO) ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] sm:text-xs font-medium bg-orange-100 text-orange-800">
                        <CalendarIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                        <span className="truncate">Agg: {format(new Date(contenuto.updatedAt), isMobile ? 'dd/MM/yy' : 'dd/MM/yyyy')}</span>
                      </span>
                    ) : (
                      <span className="text-[10px] sm:text-xs text-gray-400">
                        Data non disponibile
                      </span>
                    )}
                    <StatusBadge stato={contenuto.stato} />
                  </div>

                  {/* Responsive content layout */}
                  <div className="space-y-3 sm:space-y-4">
                    {/* Header with client info */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0 pr-2">
                        <h3 className="text-sm sm:text-base md:text-lg font-medium text-gray-900 leading-tight break-words line-clamp-2">
                          {contenuto.titolo}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-500 mt-1 break-words truncate">Cliente: {cliente?.nomeAzienda}</p>
                      </div>
                    </div>

                    {/* Content details - responsive grid on larger screens */}
                    <div className="space-y-2 sm:space-y-3 md:grid md:grid-cols-2 md:gap-4 md:space-y-0">
                      {/* Hook */}
                      <div className="text-xs sm:text-sm text-gray-700">
                        <span className="font-medium text-gray-800">Hook:</span>{' '}
                        <span className="break-words">
                          {contenuto.hook?.substring(0, isMobile ? 60 : 150) || 'N/A'}
                          {contenuto.hook && contenuto.hook.length > (isMobile ? 60 : 150) && '...'}
                        </span>
                      </div>

                      {/* Body */}
                      <div className="text-xs sm:text-sm text-gray-700">
                        <span className="font-medium text-gray-800">Body:</span>{' '}
                        <span className="break-words">
                          {contenuto.body?.substring(0, isMobile ? 60 : 150) || 'N/A'}
                          {contenuto.body && contenuto.body.length > (isMobile ? 60 : 150) && '...'}
                        </span>
                      </div>

                      {/* CTA */}
                      <div className="text-xs sm:text-sm text-gray-700 md:col-span-2">
                        <span className="font-medium text-gray-800">CTA:</span>{' '}
                        <span className="break-words">
                          {contenuto.cta?.substring(0, isMobile ? 50 : 100) || 'N/A'}
                          {contenuto.cta && contenuto.cta.length > (isMobile ? 50 : 100) && '...'}
                        </span>
                      </div>
                    </div>

                    {/* Platforms - horizontal scroll on mobile */}
                    <div className="overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                      <div className="flex gap-1.5 sm:gap-2 sm:flex-wrap min-w-min">
                        {(() => {
                          const clienteForPlatforms = getClienteById(contenuto.clienteId);
                          return clienteForPlatforms?.piattaforme?.map((piattaforma) => (
                            <span
                              key={piattaforma}
                              className="inline-flex items-center px-2 py-1 rounded-full text-[10px] sm:text-xs font-medium bg-blue-100 text-blue-800 whitespace-nowrap flex-shrink-0"
                            >
                              {piattaforma}
                            </span>
                          ));
                        })()}
                      </div>
                    </div>

                    {/* Link - touch-friendly on mobile */}
                    {contenuto.linkPubblicato && (
                      <div className="text-xs sm:text-sm pt-1">
                        <a
                          href={contenuto.linkPubblicato}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-2 sm:px-2 sm:py-1 rounded-lg sm:rounded bg-green-50 sm:bg-transparent text-green-600 hover:text-green-800 active:bg-green-100 font-medium transition-colors"
                        >
                          <Link className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">Visualizza Post</span>
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Calendar; 